import { useEffect, useRef, useState } from 'react';
import { authAPI } from '../../services/api';
import Button from '../common/Button';
import OtpInput from '../common/OtpInput';
import { Select } from '../common/FormControls';

const PROFESSIONAL_TYPES = [
  'Chartered Accountant (CA)',
  'Company Secretary (CS)',
  'Cost Accountant (CMA)',
  'Advocate / Law Firm',
  'Tax Consultant',
  'Other Professional',
];

const INDIAN_STATES = [
  'Maharashtra', 'Delhi / NCR', 'Karnataka', 'Tamil Nadu', 'Gujarat', 'Madhya Pradesh',
  'Rajasthan', 'Uttar Pradesh', 'West Bengal', 'Telangana', 'Andhra Pradesh', 'Kerala',
  'Punjab', 'Haryana', 'Bihar', 'Odisha', 'Jharkhand', 'Chhattisgarh', 'Assam', 'Goa',
  'Uttarakhand', 'Himachal Pradesh', 'Jammu & Kashmir', 'Other',
];

function normalizeMobile(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  return '';
}

function TypeCard({ active, icon, title, description, onClick }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className="relative text-left p-[14px] rounded-[12px] border-2 transition-all"
      style={{
        borderColor: active ? '#145f5f' : '#d8d1c3',
        background: active ? '#d9eceb' : '#f4f0e8',
      }}
    >
      <span
        className="absolute top-[10px] right-[10px] w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center"
        style={{ background: active ? '#145f5f' : '#d8d1c3', color: active ? '#fff' : '#7a7060' }}
      >
        &#10003;
      </span>
      <div className="text-[19px] mb-1">{icon}</div>
      <div className="text-[12.5px] font-bold text-[#08111f]">{title}</div>
      <div className="text-[10px] text-[#7a7060] leading-[1.45] mt-0.5">{description}</div>
    </button>
  );
}

function Input({ label, style: styleProp, ...props }) {
  return (
    <div className="mb-[14px]">
      <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">{label}</div>
      <input
        {...props}
        className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none"
        style={{ borderColor: '#ddd6c6', ...styleProp }}
      />
    </div>
  );
}

function FeatureRow({ icon, title, description }) {
  return (
    <div className="flex items-center gap-[13px]">
      <div
        className="w-[36px] h-[36px] rounded-[9px] border flex items-center justify-center text-[16px] flex-shrink-0"
        style={{ background: 'rgba(184,151,58,.12)', borderColor: 'rgba(184,151,58,.2)' }}
      >
        {icon}
      </div>
      <div className="text-[11.5px] text-white/70">
        <strong className="text-white/90">{title}</strong> - {description}
      </div>
    </div>
  );
}

/**
 * Password strength calculator
 * Returns: 0 (empty), 1 (weak), 2 (fair), 3 (good), 4 (strong)
 */
function getPasswordStrength(pwd) {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;
  return score;
}

const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLORS = ['', '#9a564a', '#8a6a1f', '#145f5f', '#4a6b3f'];


export default function LoginForm({
  onPasswordLogin,
  onRequestOtp,
  onVerifyOtp,
  onResendOtp,
  // Email-OTP 2FA — engaged when the backend returns stage='awaiting-email-otp'.
  // The 2FA email also contains a one-click magic link.
  onVerifyEmailOtp,
  onResendEmailOtp,
}) {
  const [mode, setMode] = useState('login');
  const [userType, setUserType] = useState('professional');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [otpFlow, setOtpFlow] = useState(false);
  const [otpMobile, setOtpMobile] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpTimer, setOtpTimer] = useState(30);
  const [otpTimerActive, setOtpTimerActive] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  // OTP "kind" — 'sms' (Twilio mobile path) or 'email' (post-password 2FA).
  // UI is shared; verify/resend branch on this in verifyOtpLogin/resendOtpLogin.
  const [otpKind, setOtpKind] = useState('sms');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpEmailHint, setOtpEmailHint] = useState('');
  const [registerBusy, setRegisterBusy] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const otpRef = useRef(null);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    mobile: '',
    state: '',
    professionalType: '',
    customProfessionalType: '',
    membership: '',
    firm: '',
    designation: '',
    cin: '',
    company: '',
    password: '',
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const mobileForOtp = normalizeMobile(loginData.email);

  const passwordStrength = mode === 'register' ? getPasswordStrength(registerData.password) : 0;

  useEffect(() => {
    if (!otpFlow || !otpTimerActive || otpTimer <= 0) {
      if (otpTimer <= 0) setOtpTimerActive(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setOtpTimer((prev) => prev - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [otpFlow, otpTimer, otpTimerActive]);

  const resetOtpState = () => {
    setOtpFlow(false);
    setOtpKind('sms');
    setOtpIdentifier('');
    setOtpEmailHint('');
    setOtpBusy(false);
    setOtpTimer(30);
    setOtpTimerActive(false);
    setOtpCode('');
  };

  // ==================== LOGIN HANDLERS ====================

  const submitLogin = async (event) => {
    event.preventDefault();
    if (otpFlow) return;
    if (!loginData.email || !loginData.password) {
      setError('Please enter email/mobile and password');
      return;
    }
    setError('');
    setLoginBusy(true);
    try {
      const response = await onPasswordLogin?.(loginData.email.trim(), loginData.password, rememberMe);
      if (!response?.success) {
        throw new Error(response?.message || 'Login failed');
      }
      // Backend returned 2FA gate — switch to OTP entry.
      if (response.stage === 'awaiting-email-otp') {
        setOtpKind('email');
        setOtpIdentifier(response.data?.identifier || loginData.email.trim());
        setOtpEmailHint(response.data?.emailHint || '');
        setOtpCode('');
        setOtpTimer(response.data?.canResendInSeconds || 60);
        setOtpTimerActive(true);
        setOtpFlow(true);
        window.setTimeout(() => otpRef.current?.focus(), 0);
      }
      // No stage → parent already navigated; nothing to do here.
    } catch (loginError) {
      setError(loginError.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoginBusy(false);
    }
  };

  // Magic-link is bundled into the 2FA OTP email itself — no standalone trigger here.

  // ==================== REGISTER HANDLER ====================
  const submitRegister = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!registerData.name || !registerData.email || !registerData.mobile || !registerData.password) {
      setError('Please fill all required fields: name, email, mobile, and password');
      return;
    }

    if (userType === 'professional') {
      if (!registerData.professionalType) { setError('Professional type is required'); return; }
      if (registerData.professionalType === 'Other Professional' && !registerData.customProfessionalType.trim()) { setError('Please specify your professional type'); return; }
      if (!registerData.membership.trim()) { setError('Membership / Registration number is required'); return; }
      if (!registerData.firm.trim()) { setError('Firm / Practice name is required'); return; }
    }

    if (userType === 'corporate') {
      if (!registerData.designation.trim()) { setError('Designation is required'); return; }
      if (!registerData.cin.trim()) { setError('Company CIN is required'); return; }
      if (!registerData.company.trim()) { setError('Company name is required'); return; }
    }

    if (!registerData.state) { setError('State is required'); return; }

    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(registerData.email)) { setError('Please provide a valid email address'); return; }

    const mobile = registerData.mobile.replace(/\D/g, '');
    if (mobile.length !== 10 || !/^[6-9]/.test(mobile)) { setError('Please provide a valid 10-digit mobile number starting with 6-9'); return; }

    if (registerData.password.length < 8) { setError('Password must be at least 8 characters long'); return; }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/.test(registerData.password)) {
      setError('Password must contain uppercase, lowercase, number, and special character');
      return;
    }

    if (userType === 'corporate' && registerData.cin) {
      if (!/^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(registerData.cin)) { setError('Please provide a valid CIN (e.g., U72900MH2019PTC324567)'); return; }
    }

    if (!termsAccepted) { setError('You must accept the Terms of Service and Privacy Policy to register'); return; }

    setRegisterBusy(true);
    try {
      const registrationData = {
        fullName: registerData.name.trim(),
        email: registerData.email.trim(),
        mobile: mobile,
        password: registerData.password,
        userType: userType,
        state: registerData.state
      };

      if (userType === 'professional') {
        registrationData.professionalType = registerData.professionalType;
        if (registerData.professionalType === 'Other Professional') {
          registrationData.customProfessionalType = registerData.customProfessionalType.trim();
        }
        registrationData.membership = registerData.membership.trim();
        registrationData.firm = registerData.firm.trim();
      } else {
        registrationData.designation = registerData.designation.trim();
        registrationData.cin = registerData.cin.trim();
        registrationData.company = registerData.company.trim();
      }

      const result = await authAPI.register(registrationData);
      if (!result.success) throw new Error(result.message || 'Registration failed');

      setSuccess('Registration successful! You can now sign in with your credentials.');
      setTimeout(() => {
        setMode('login');
        setRegisterData({
          name: '',
          email: '',
          mobile: '',
          state: '',
          professionalType: '',
          customProfessionalType: '',
          membership: '',
          firm: '',
          designation: '',
          cin: '',
          company: '',
          password: '',
        });
        setTermsAccepted(false);
        setSuccess('');
      }, 2000);
    } catch (registerError) {
      setError(registerError.message || 'Registration failed. Please try again.');
    } finally {
      setRegisterBusy(false);
    }
  };

  // ==================== FORGOT PASSWORD HANDLER ====================

  const submitForgot = async (event) => {
    event.preventDefault();
    if (!forgotEmail) { setError('Please enter your registered email'); return; }
    setError('');
    setSuccess('');
    setForgotBusy(true);
    setForgotSent(false);
    try {
      await authAPI.forgotPassword(forgotEmail.trim());
    } catch (_) {
      // Swallow — for security we always show the same confirmation regardless
      // of whether the email exists, so attackers can't enumerate accounts.
    } finally {
      setForgotSent(true);
      setForgotBusy(false);
    }
  };

  // ==================== OTP HANDLERS ====================

  const requestOtpLogin = async () => {
    if (!mobileForOtp) { setError('Enter a valid 10-digit mobile number to use OTP sign in'); return; }
    if (typeof onRequestOtp !== 'function') { setError('OTP sign in is currently unavailable'); return; }
    setError('');
    setOtpBusy(true);
    try {
      const response = await onRequestOtp(mobileForOtp);
      if (!response?.success) throw new Error(response?.message || 'Failed to send OTP.');
      setOtpKind('sms');
      setOtpMobile(mobileForOtp);
      setOtpFlow(true);
      setOtpCode('');
      setOtpTimer(30);
      setOtpTimerActive(true);
      window.setTimeout(() => otpRef.current?.focus(), 0);
    } catch (err) { setError(err.message || 'Failed to send OTP.'); }
    finally { setOtpBusy(false); }
  };

  // `event` is optional — OtpInput auto-submits via onComplete (no form event).
  // `codeOverride` carries the just-completed code so we don't race React state.
  const verifyOtpLogin = async (event, codeOverride) => {
    event?.preventDefault?.();
    const code = codeOverride ?? otpCode;
    if (code.length !== 6 && code !== '00000' && code !== '000000') { setError('Please enter all 6 digits'); return; }
    setError('');
    setOtpBusy(true);
    try {
      let response;
      if (otpKind === 'email') {
        if (typeof onVerifyEmailOtp !== 'function') { setError('Email OTP verification is currently unavailable'); return; }
        response = await onVerifyEmailOtp(otpIdentifier, code);
      } else {
        if (typeof onVerifyOtp !== 'function') { setError('OTP verification is currently unavailable'); return; }
        response = await onVerifyOtp(otpMobile, code);
      }
      if (!response?.success) throw new Error(response?.message || 'Invalid OTP.');
    } catch (err) { setError(err.message || 'Invalid OTP. Please try again.'); }
    finally { setOtpBusy(false); }
  };

  const resendOtpLogin = async () => {
    if (!otpFlow || otpTimerActive || otpBusy) return;
    setError('');
    setOtpBusy(true);
    try {
      let response;
      if (otpKind === 'email') {
        if (typeof onResendEmailOtp !== 'function') { setError('OTP resend is currently unavailable'); return; }
        response = await onResendEmailOtp(otpIdentifier);
      } else {
        if (typeof onResendOtp !== 'function') { setError('OTP resend is currently unavailable'); return; }
        response = await onResendOtp(otpMobile);
      }
      if (!response?.success) throw new Error(response?.message || 'Failed to resend OTP.');
      setOtpCode('');
      setOtpTimer(otpKind === 'email' ? 60 : 30);
      setOtpTimerActive(true);
      otpRef.current?.focus();
    } catch (err) { setError(err.message || 'Failed to resend OTP.'); }
    finally { setOtpBusy(false); }
  };

  const formatOtpTimer = () => {
    const mins = Math.floor(otpTimer / 60);
    const secs = otpTimer % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // ==================== RENDER ====================

  return (
    <div className="h-[var(--app-vh)] w-full flex overflow-hidden" style={{ background: '#f4f0e8' }}>
      {/* Left panel - branding */}
      <div className="relative hidden lg:flex h-[var(--app-vh)] flex-1 px-[52px] py-12 flex-col justify-between overflow-hidden" style={{ background: 'linear-gradient(135deg,#0a1628 0%,#0d2a3a 40%,#0a3d2a 100%)' }}>
        <div className="absolute inset-0" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div className="absolute -bottom-[120px] -left-[80px] w-[500px] h-[500px] pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(184,151,58,.12) 0%,transparent 70%)' }} />

        <div className="relative z-[1]">
          <div className="flex items-center gap-[14px] mb-12">
            <img src="/complyv-logo.svg" alt="ComplyV" className="w-12 h-12 object-contain" />
            <div>
              <div className="text-[24px] font-bold leading-none" style={{ color: '#b8973a', fontFamily: "'Cormorant Garamond', serif" }}>ComplyV</div>
              <div className="text-[8.5px] tracking-[3px] uppercase text-white/30 mt-1">One-Stop Compliance &amp; Business Platform</div>
            </div>
          </div>
          <div className="text-[38px] leading-[1.15] font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            India's Smartest<br /><span style={{ color: '#b8973a' }}>Compliance</span><br />Platform
          </div>
          <p className="mt-[18px] text-[13px] leading-[1.7] text-white/55 max-w-[420px]">
            Manage 127+ companies, draft AI-powered minutes &amp; resolutions, track MCA deadlines, and automate annual filings - all in one place.
          </p>
          <div className="mt-9 space-y-[14px]">
            <FeatureRow icon="&#129302;" title="AI Document Drafting" description="Board minutes, resolutions, affidavits with company letterhead" />
            <FeatureRow icon="&#128197;" title="AGM Due Date Engine" description="AOC-4, MGT-7, ADT-1 auto-calculated from AGM date" />
            <FeatureRow icon="&#127970;" title="MCA21 Integration" description="Fetch company data instantly with CIN or upload master export" />
            <FeatureRow icon="&#128220;" title="100+ Resolution Templates" description="Every business transaction, fully editable & printable" />
          </div>
        </div>
        <div className="relative z-[1]">
          <div className="flex gap-8 text-center text-white">
            {[['127+', 'Companies'], ['100+', 'Doc Templates'], ['SS-1/2', 'Compliant'], ['24/7', 'AI Support']].map(([val, lbl]) => (
              <div key={lbl}><div className="text-[25px] font-bold leading-none" style={{ color: '#b8973a', fontFamily: "'Cormorant Garamond', serif" }}>{val}</div><div className="text-[9.5px] text-white/35 mt-[3px] uppercase tracking-[1.2px]">{lbl}</div></div>
            ))}
          </div>
          <div className="mt-6 text-[10.5px] text-white/20">&copy; 2026 ComplyV &middot; Companies Act 2013 &middot; LLP Act 2008 &middot; SEBI &middot; DPDP 2023</div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="w-full lg:w-[460px] h-[var(--app-vh)] bg-[#f4f0e8] overflow-y-auto px-7 md:px-12 py-10 md:py-[52px]">
        <div className="w-full max-w-[364px] mx-auto">
          <div className="text-[26px] font-bold mb-[6px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{mode === 'register' ? 'Create Account' : mode === 'forgot' ? 'Reset Password' : 'Welcome Back'}</div>
          <div className="text-[12.5px] text-[#7a7060] mb-6">
            {mode === 'register' ? 'Register your ComplyV account' : mode === 'forgot' ? 'Enter your registered email and we will send a reset link' : 'Sign in to your ComplyV account'}
          </div>

          {mode === 'register' && (
            <div className="grid grid-cols-2 gap-[10px] mb-6">
              <TypeCard active={userType === 'professional'} icon="&#128104;&zwj;&#128188;" title="Professional" description="CA, CS, CMA & law firms managing multiple client companies" onClick={() => setUserType('professional')} />
              <TypeCard active={userType === 'corporate'} icon="&#127970;" title="Corporate" description="Company directors, CFOs & in-house compliance teams" onClick={() => setUserType('corporate')} />
            </div>
          )}

          {success && <div className="mb-3 text-[12.5px] px-3 py-2 rounded-lg border bg-[#e8efe5] border-[#d3e2cb] text-[#4a6b3f]">{success}</div>}
          {error && <div className="mb-3 text-[12.5px] px-3 py-2 rounded-lg border bg-[#f2e9e6] border-[#e6d2cb] text-[#9a564a]">{error}</div>}

          {/* ==================== LOGIN ==================== */}
          {mode === 'login' && (
            <form onSubmit={otpFlow ? verifyOtpLogin : submitLogin}>
              <Input label="Email / Mobile" type="text" placeholder="you@example.com or 9876543210" value={loginData.email} onChange={(e) => setLoginData((p) => ({ ...p, email: e.target.value }))} />
              {!otpFlow && mobileForOtp && <div className="-mt-2 mb-3 text-[11.5px] text-[#145f5f]">Mobile detected. You can sign in with OTP instead of password.</div>}

              {!otpFlow ? (
                <>
                  <div className="mb-[14px]">
                    <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">Password</div>
                    <div className="relative">
                      <input type={passwordVisible ? 'text' : 'password'} placeholder="Enter your password" value={loginData.password} onChange={(e) => setLoginData((p) => ({ ...p, password: e.target.value }))} className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none pr-10" style={{ borderColor: '#ddd6c6' }} />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-[14px] opacity-50 hover:opacity-100" onClick={() => setPasswordVisible((p) => !p)}>&#128065;</button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11.5px] mb-3 text-[#7a7060]">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="accent-[#145f5f]" /> Remember me</label>
                    <button type="button" className="underline" onClick={() => {
                      // Carry over whatever the user already typed in the sign-in
                      // field — but only if it looks like an email (the field
                      // also accepts mobile numbers). Saves them retyping.
                      const typed = (loginData.email || '').trim();
                      if (typed.includes('@')) setForgotEmail(typed);
                      setMode('forgot');
                      setError('');
                      setSuccess('');
                    }}>Forgot password?</button>
                  </div>
                  <Button type="submit" block loading={loginBusy}>{loginBusy ? 'Signing In...' : 'Sign In \u2192'}</Button>
                  {mobileForOtp && (
                    <Button type="button" variant="outline" block loading={otpBusy} className="mt-2" onClick={requestOtpLogin}>
                      {otpBusy ? 'Sending OTP...' : 'Sign In with OTP'}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-3 p-3 rounded-lg border" style={{ borderColor: '#ddd6c6', background: '#f8f5ef' }}>
                    <div className="text-[10.5px] uppercase tracking-[.8px] text-[#7a7060]">
                      {otpKind === 'email' ? 'Sign-in code sent to' : 'OTP sent to'}
                    </div>
                    <div className="text-[12.5px] font-bold text-[#08111f]">
                      {otpKind === 'email' ? (otpEmailHint || otpIdentifier) : `+91 ${otpMobile}`}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">Enter OTP</div>
                    <OtpInput
                      ref={otpRef}
                      length={6}
                      value={otpCode}
                      onChange={(code) => { setOtpCode(code); if (error) setError(''); }}
                      onComplete={(code) => verifyOtpLogin(undefined, code)}
                      disabled={otpBusy}
                      error={Boolean(error)}
                    />
                    <div className="mt-1.5 text-[10.5px] text-[#a39a88]">
                      Paste the code or type it — it submits automatically.
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11.5px] mb-3 text-[#7a7060]">
                    <span>{otpTimerActive ? `Resend in ${formatOtpTimer()}` : 'OTP expired'}</span>
                    <button type="button" className="underline disabled:no-underline disabled:opacity-50" onClick={resendOtpLogin} disabled={otpTimerActive || otpBusy}>Resend OTP</button>
                  </div>
                  <Button type="submit" block loading={otpBusy}>{otpBusy ? 'Verifying...' : 'Verify OTP \u2192'}</Button>
                  {/* Magic-link nudge \u2014 link is in the 2FA email. */}
                  {otpKind === 'email' && (
                    <div className="mt-3 p-3 rounded-[10px] text-[11.5px] leading-relaxed" style={{ background: '#fdfbf6', border: '1px solid #e8e2d6', color: '#5a4e3a' }}>
                      <span className="inline-block align-middle mr-1.5 text-[13px]">&#128274;</span>
                      Don't want to type the code? Open the email we just sent
                      and click the <strong>Sign in with one click</strong> button.
                      The link is single-use and expires in 15 minutes.
                    </div>
                  )}
                  <Button type="button" variant="outline" block className="mt-2" onClick={resetOtpState}>
                    {otpKind === 'email' ? 'Cancel sign-in' : 'Use password instead'}
                  </Button>
                </>
              )}

              {/* Demo buttons - dev only, autofill credentials (no auth bypass) */}
              {process.env.NODE_ENV === 'development' && !otpFlow && (
                <>
                  <div className="text-center text-[11.5px] text-[#7a7060] my-[18px]">or quick demo login</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" variant="outline" disabled={loginBusy}
                      onClick={() => setLoginData({ email: 'demo.ca@compliancesarathi.in', password: 'Demo@1234' })}>&#128104;&zwj;&#128188; Demo - CA/CS</Button>
                    <Button type="button" variant="outline" disabled={loginBusy}
                      onClick={() => setLoginData({ email: 'demo.corp@compliancesarathi.in', password: 'Demo@1234' })}>&#127970; Demo - Corporate</Button>
                  </div>
                </>
              )}

              <div className="text-center text-[12px] text-[#7a7060] mt-4">
                Don't have an account?{' '}
                <button type="button" className="font-bold" style={{ color: '#145f5f' }} onClick={() => { setMode('register'); setError(''); setSuccess(''); }}>Create account &rarr;</button>
              </div>
            </form>
          )}

          {/* ==================== REGISTER ==================== */}
          {mode === 'register' && (
            <form onSubmit={submitRegister}>
              <Input label="Full Name *" type="text" placeholder="CA Priya Sharma / Rajesh Kumar" value={registerData.name} onChange={(e) => setRegisterData((p) => ({ ...p, name: e.target.value }))} />

              {userType === 'professional' ? (
                <>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="mb-[14px]">
                      <div className="text-[11.5px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">Professional Type *</div>
                      <Select
                        value={registerData.professionalType}
                        placeholder="Select Professional Type"
                        options={PROFESSIONAL_TYPES}
                        onChange={(t) => setRegisterData((p) => ({ ...p, professionalType: t, customProfessionalType: t === 'Other Professional' ? p.customProfessionalType : '' }))}
                      />
                    </div>
                    {registerData.professionalType === 'Other Professional' && (
                      <Input label="Specify Professional Type *" type="text" placeholder="e.g., Financial Advisor, Auditor" value={registerData.customProfessionalType} onChange={(e) => setRegisterData((p) => ({ ...p, customProfessionalType: e.target.value }))} />
                    )}
                    <Input label="Membership / Reg. No. *" type="text" placeholder="ICAI: 123456 / ICSI: A12345" value={registerData.membership} onChange={(e) => setRegisterData((p) => ({ ...p, membership: e.target.value }))} />
                  </div>
                  <Input label="Firm / Practice Name *" type="text" placeholder="M/s. Sharma & Associates" value={registerData.firm} onChange={(e) => setRegisterData((p) => ({ ...p, firm: e.target.value }))} />
                </>
              ) : (
                <>
                  <Input label="Designation *" type="text" placeholder="Managing Director" value={registerData.designation} onChange={(e) => setRegisterData((p) => ({ ...p, designation: e.target.value }))} />
                  <Input label="Company CIN *" type="text" placeholder="U72900MH2019PTC324567" value={registerData.cin} onChange={(e) => setRegisterData((p) => ({ ...p, cin: e.target.value.toUpperCase() }))} />
                  <Input label="Company Name *" type="text" placeholder="TECHWAVE SOLUTIONS PRIVATE LIMITED" value={registerData.company} onChange={(e) => setRegisterData((p) => ({ ...p, company: e.target.value.toUpperCase() }))} />
                </>
              )}

              <Input label="Email Address *" type="email" placeholder="you@example.com" value={registerData.email} onChange={(e) => setRegisterData((p) => ({ ...p, email: e.target.value }))} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Mobile Number *" type="tel" placeholder="+91 98765 43210" value={registerData.mobile} onChange={(e) => setRegisterData((p) => ({ ...p, mobile: e.target.value }))} />
                <div className="mb-[14px]">
                  <div className="text-[11.5px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">State *</div>
                  {/* 24 states — past Select's searchThreshold, so this one
                      opens with a type-to-filter box instead of a long scroll. */}
                  <Select
                    value={registerData.state}
                    placeholder="Select State"
                    options={INDIAN_STATES}
                    onChange={(v) => setRegisterData((p) => ({ ...p, state: v }))}
                  />
                </div>
              </div>

              {/* Password with strength indicator */}
              <div className="mb-[14px]">
                <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">Create Password *</div>
                <input type="password" placeholder="Min. 8 chars, A-z, 0-9, !@#" value={registerData.password} onChange={(e) => setRegisterData((p) => ({ ...p, password: e.target.value }))}
                  className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none" style={{ borderColor: '#ddd6c6' }} />
                {registerData.password && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-[3px] rounded-full bg-gray-200 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(passwordStrength / 4) * 100}%`, background: STRENGTH_COLORS[passwordStrength] }} />
                    </div>
                    <span className="text-[10px] font-semibold" style={{ color: STRENGTH_COLORS[passwordStrength] }}>{STRENGTH_LABELS[passwordStrength]}</span>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2 text-[12px] text-[#7a7060] mb-4 cursor-pointer">
                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-[2px] accent-[#145f5f]" />
                <span>I agree to the <span className="underline text-[#145f5f]">Terms of Service</span> and <span className="underline text-[#145f5f]">Privacy Policy</span>. ComplyV complies with DPDP Act 2023.</span>
              </label>

              <Button type="submit" block size="lg" loading={registerBusy}>
                {registerBusy ? 'Creating Account...' : 'Create Account \u2192'}
              </Button>
              <div className="text-center text-[12.5px] text-[#7a7060] mt-4">
                Already have an account?{' '}
                <button type="button" className="font-bold" style={{ color: '#145f5f' }} onClick={() => { setMode('login'); setError(''); setSuccess(''); }}>Sign in &rarr;</button>
              </div>
            </form>
          )}

          {/* ==================== FORGOT PASSWORD ==================== */}
          {mode === 'forgot' && (
            <form onSubmit={submitForgot}>
              {/* Brand icon tile \u2014 replaces the bare \ud83d\udd12 emoji. Soft cream tile,
                  teal padlock with a gold keyhole dot. Hidden once the link is
                  sent so the success banner becomes the hero. */}
              {/* Compact hero: lock tile inline with reassurance copy \u2014 no floating
                  orphan icon, no dead vertical space. */}
              {!forgotSent && (
                <div
                  className="flex items-center gap-3 rounded-[12px] border px-[14px] py-[12px] mb-[16px]"
                  style={{ background: 'linear-gradient(135deg,#fdfbf6 0%,#f6f0e2 100%)', borderColor: '#e8dcc0' }}
                >
                  <div
                    className="flex-none rounded-[12px] flex items-center justify-center"
                    style={{
                      width: 44,
                      height: 44,
                      background: '#ffffff',
                      border: '1px solid #e8dcc0',
                      boxShadow: '0 3px 10px rgba(20,95,95,.07)',
                    }}
                    aria-hidden="true"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#145f5f" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3.5" y="11" width="17" height="10.5" rx="2.2" />
                      <path d="M7.5 11V7.2a4.5 4.5 0 0 1 9 0V11" />
                      <circle cx="12" cy="16.2" r="1.4" fill="#b8973a" stroke="none" />
                      <line x1="12" y1="16.2" x2="12" y2="18.4" stroke="#b8973a" strokeWidth="1.6" />
                    </svg>
                  </div>
                  <div className="text-[11.5px] leading-[1.5] text-[#5a4e3a]">
                    No stress \u2014 this happens to everyone. Enter your registered
                    email and we'll send a secure link to set a new password.
                  </div>
                </div>
              )}

              <Input
                label="Registered Email"
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotSent}
                style={forgotSent ? { borderColor: '#ddd6c6', background: '#f8f5ef', color: '#7a7060' } : undefined}
              />

              {/* "How it works" \u2014 connected timeline, quieter than a boxed list */}
              {!forgotSent && (
                <div className="px-[2px] mb-[16px]">
                  {[
                    'We email you a secure reset link',
                    'Open it within 1 hour to choose a new password',
                    'Sign in with the new password \u2014 that\u2019s it',
                  ].map((step, i, arr) => (
                    <div key={i} className="flex gap-[10px]">
                      {/* Rail: numbered dot + connecting line */}
                      <div className="flex flex-col items-center">
                        <span
                          className="flex-none inline-flex items-center justify-center rounded-full font-bold"
                          style={{
                            width: 18, height: 18, fontSize: 9.5, lineHeight: 1,
                            background: i === 0 ? '#145f5f' : '#e4efee',
                            color: i === 0 ? '#fff' : '#145f5f',
                            border: i === 0 ? 'none' : '1px solid #c9dedb',
                          }}
                        >
                          {i + 1}
                        </span>
                        {i < arr.length - 1 && (
                          <span className="w-[1.5px] flex-1 my-[3px]" style={{ background: '#dfe8e6', minHeight: 10 }} />
                        )}
                      </div>
                      <div className="text-[11.5px] text-[#5a4e3a] leading-[1.5] pb-[10px]">{step}</div>
                    </div>
                  ))}
                </div>
              )}

              <Button
                type="submit"
                block
                size="lg"
                variant={forgotSent ? 'success' : 'primary'}
                loading={forgotBusy}
                disabled={forgotSent}
              >
                {forgotBusy ? 'Sending\u2026' : forgotSent ? 'Link Sent \u2713' : 'Send Reset Link \u2192'}
              </Button>

              {forgotSent && (
                <div
                  className="mt-4 rounded-[12px] border overflow-hidden"
                  style={{ borderColor: '#cfe4df', background: '#f0f7f5' }}
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-start gap-3 px-[14px] py-[12px]">
                    <div
                      className="flex-none flex items-center justify-center rounded-full"
                      style={{ width: 28, height: 28, background: '#145f5f', color: '#fff', fontSize: 14, fontWeight: 700 }}
                      aria-hidden="true"
                    >
                      &#10003;
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-bold text-[#0d4848] leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Check your inbox
                      </div>
                      <div className="text-[11.5px] text-[#5a4e3a] mt-[2px] leading-snug">
                        If <span className="font-semibold text-[#08111f]">{forgotEmail}</span> is registered, a reset link is on its way. Check spam too if you don't see it in a minute.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setMode('login'); setError(''); setSuccess(''); setForgotSent(false); setForgotEmail(''); }}
                >
                  &larr; Back to Sign In
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
